import type { Region } from "@/data/regions";

/**
 * Unieke, zoekwoordrijke meta description per werkgebied.
 * Gebruikt de eigen intro-tekst van de plaats, zodat geen twee pagina's
 * dezelfde description krijgen (dubbele content voorkomen).
 */
type Focus = { title: string; description: string; block: SeoBlock };

/** Extra focus voor steden die al bijna in de top 3 van Google staan. */
const FOCUS: Record<string, Focus> = {
  "pijnacker-nootdorp": {
    title: "Verhuisbedrijf Pijnacker & Nootdorp | Verhuizers met verhuislift",
    description:
      "Verhuisbedrijf in Pijnacker, Nootdorp en Delfgauw nodig? Eigen verhuizers, verhuislift vanaf € 95 p/u en een heldere offerte op maat. Trustoo 9,9. Bel 085 060 1023.",
    block: {
      h2: "Verhuisbedrijf Pijnacker: waarom kiezen voor Vakkundig?",
      body: "Als verhuisbedrijf in Pijnacker kennen we de nieuwbouw in Ackerswoude en Keijzershof, de rijtjeshuizen in Pijnacker Centrum en de woningen in Nootdorp en Delfgauw. Verhuis je binnen Pijnacker, van Den Haag of Delft naar Pijnacker, of juist de gemeente uit? Wij regelen het met eigen vaste verhuizers, wikkelfolie en dekens, en zo nodig onze verhuislift vanaf € 95 per uur. Je krijgt één aanspreekpunt en een duidelijke prijs vooraf.",
    },
  },
  wassenaar: {
    title: "Verhuislift huren Wassenaar vanaf € 95 p/u | Met bediener",
    description:
      "Verhuislift huren in Wassenaar vanaf € 95 per uur inclusief ervaren bediener, tot 26 meter. Ook complete verhuizingen van villa's en grote woningen. Bel 085 060 1023.",
    block: {
      h2: "Verhuislift huren in Wassenaar: zo werkt het",
      body: "Onze verhuislift in Wassenaar huur je vanaf € 95 per uur, altijd met een eigen ervaren bediener. De lift reikt tot 26 meter en is ideaal voor balkons, dakterrassen en ramen op de bovenverdieping in Oostdorp, Rijksdorp, De Kieviet en het centrum. Stuur gerust een foto van de gevel via WhatsApp, dan kijken we gratis of de lift kan staan. Ook voor banken, kasten, piano's en witgoed die niet door het trappenhuis passen.",
    },
  },
  "leidschendam-voorburg": {
    title: "Bedrijfsverhuizing Voorburg & Leidschendam | Zonder stilstand",
    description:
      "Bedrijfsverhuizing of kantoorverhuizing in Voorburg of Leidschendam? Vrijdag inpakken, maandag doorwerken. Offerte binnen 24 uur, vaste totaalprijs. Bel 085 060 1023.",
    block: {
      h2: "Bedrijfsverhuizing in Voorburg zonder stilstand",
      body: "Voor een bedrijfsverhuizing in Voorburg of Leidschendam plannen we alles rond jullie werktijden: in de avond of het weekend, zodat jullie maandag gewoon doorwerken. We verhuizen kantoren, praktijken en winkels, labelen per werkplek en zetten bureaus en kasten direct op de juiste plek. Opdrachtgevers als Gemeente Den Haag en World Forum vertrouwen op ons. Je krijgt binnen 24 uur een offerte met één vaste totaalprijs.",
    },
  },
  rijswijk: {
    title: "Verhuisbedrijf Rijswijk | Verhuizers & verhuislift € 95 p/u",
    description:
      "Verhuizer in Rijswijk nodig? Eigen verhuizers, verhuislift huren vanaf € 95 per uur met bediener en een heldere offerte op maat. Trustoo 9,9. Bel 085 060 1023.",
    block: {
      h2: "Verhuizer in Rijswijk: waarom kiezen voor Vakkundig?",
      body: "Als verhuisbedrijf in Rijswijk kennen we de herenhuizen in Oud-Rijswijk, de nieuwbouw in Rijswijk-Buiten, de flats in Steenvoorde en Leeuwendaal en de kantoren op de Plaspoelpolder. Onze verhuizers in Rijswijk werken met wikkelfolie en verhuisdekens, en waar het trappenhuis te smal is zetten we onze verhuislift in vanaf € 95 per uur inclusief bediener. Rijswijk ligt om de hoek van onze basis, dus we plannen flexibel en je krijgt één vast aanspreekpunt en een duidelijke prijs vooraf.",
    },
  },
  "den-haag": {
    title: "Verhuisbedrijf Den Haag | Verhuizers & verhuislift € 95 p/u",
    description:
      "Verhuisbedrijf in Den Haag nodig? Eigen verhuizers, verhuislift vanaf € 95 per uur met bediener, inpakservice en opslag. Trustoo 9,9. Gratis offerte: 085 060 1023.",
    block: {
      h2: "Verhuisbedrijf Den Haag: vakkundig verhuizen in elke wijk",
      body: "Den Haag is onze thuisbasis. Onze verhuizers kennen de herenhuizen in Statenkwartier en Benoordenhout, de portiekflats in Bezuidenhout en Segbroek, de smalle straten in het Centrum en de appartementen in Scheveningen. Bovenwoning zonder lift of een bank die niet door het trappenhuis past? Dan zetten we onze verhuislift in vanaf € 95 per uur, inclusief ervaren bediener. We denken vooraf mee over parkeren en een eventuele ontheffing, zodat je verhuizing in Den Haag op de dag zelf soepel verloopt.",
    },
  },
};

/**
 * Unieke, zoekwoordrijke meta description per werkgebied.
 * Gebruikt de eigen intro-tekst van de plaats, zodat geen twee pagina's
 * dezelfde description krijgen (dubbele content voorkomen).
 */
export function regionMetaDescription(region: Region): string {
  const focus = FOCUS[region.slug];
  if (focus) return focus.description;
  const base = `Verhuisbedrijf ${region.city} · ${region.intro.replace(/\s+/g, " ").trim()}`;
  const tail = ` Verhuizen in ${region.city} met eigen verhuizers, verhuislift huren, inpakservice, opslag, seniorenverhuizing, kantoorverhuizing en woningontruiming. 4,9/5 uit 507 reviews. Bel 085 060 1023.`;
  const max = 320;
  const text = `${base}${tail}`;
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** Korte, zoekwoordrijke titel per werkgebied. */
export function regionTitle(region: Region): string {
  const focus = FOCUS[region.slug];
  if (focus) return focus.title;
  const full = `Verhuisbedrijf ${region.city} | Verhuizen, verhuislift & opslag`;
  return full.length <= 60 ? full : `Verhuisbedrijf ${region.city} | Verhuizers & verhuislift`;
}

export type SeoBlock = { h2: string; body: string };

/**
 * Extra unieke SEO-tekstblokken per plaats: natuurlijke zoekwoordvarianten
 * ("verhuizers", "verhuisbedrijf", "verhuislift huren", "opslag", "ontruiming")
 * gecombineerd met echte wijk- en regiogegevens van die plaats.
 */
export function regionSeoBlocks(region: Region): SeoBlock[] {
  const city = region.city;
  const hoods = region.neighbourhoods;
  const hoodList = hoods.slice(0, 4).join(", ");
  const lastHood = hoods[hoods.length - 1] ?? city;
  const focus = FOCUS[region.slug];

  return [
    ...(focus ? [focus.block] : []),
    {
      h2: `Verhuizers in ${city} die het werk uit handen nemen`,
      body: `Zoek je een verhuisbedrijf in ${city} dat gewoon doet wat het belooft? Onze verhuizers komen dagelijks in ${region.group} en kennen de straten, de opritten en de trappenhuizen van ${hoodList} en ${lastHood}. Je krijgt één vast aanspreekpunt, een duidelijke offerte op maat en een ervaren team dat je meubels met wikkelfolie en verhuisdekens beschermt. Van een studio in het centrum van ${city} tot een gezinswoning met zolder: wij plannen de verhuizing zo dat je op dezelfde dag klaar bent.`,
    },
    {
      h2: `Verhuislift huren in ${city} en opslag om de hoek`,
      body: `Past de bank niet door het trappenhuis? Dan zetten we in ${city} onze verhuislift in met eigen bediening, tot acht hoog en 26 meter. Vaak hoeft er daardoor minder gedemonteerd te worden en gaat het laden en lossen sneller. Is de nieuwe woning nog niet klaar, dan slaan we je inboedel droog en beveiligd op en brengen we alles terug wanneer het jou uitkomt. Ook voor inpakservice, demontage en montage van kasten en bedden en voor een complete woningontruiming in ${city} ben je bij ons aan het juiste adres.`,
    },
    {
      h2: `Zakelijke verhuizing en transport in ${city}`,
      body: `Naast particuliere verhuizingen in ${city} verzorgen wij kantoor- en bedrijfsverhuizingen, seniorenverhuizingen, evenementenlogistiek en speciaal transport van bijvoorbeeld een piano of witgoed. We werken zeven dagen per week van 09:00 tot 18:00, ook 's avonds en in het weekend, zodat jouw bedrijf in ${city} zo kort mogelijk stilstaat. Vraag online een gratis offerte aan of stuur een WhatsApp-bericht; je hoort meestal dezelfde dag wat een verhuizing in ${city} kost.`,
    },
  ];
}

/** Unieke SEO-tekst per regiogroep voor het werkgebiedoverzicht. */
export const groupSeoText: Record<string, string> = {
  Haaglanden:
    "In Haaglanden zijn wij thuis: Den Haag, Rijswijk, Delft, Zoetermeer, Leidschendam-Voorburg, Wassenaar en Pijnacker-Nootdorp. Smalle binnenstadstraten, portiekflats zonder lift en betaald parkeren zijn dagelijkse kost, dus we plannen de wagen en de verhuislift vooraf in.",
  "Rotterdam-Rijnmond":
    "Rotterdam-Rijnmond betekent veel hoogbouw en appartementen met galerij. Onze verhuislift gaat tot acht hoog en 26 meter, waardoor er minder gedemonteerd hoeft te worden en je verhuizing in Rotterdam, Schiedam, Vlaardingen of Capelle vlot verloopt.",
  "Leiden en Bollenstreek":
    "Leiden, Oegstgeest, Voorschoten, Katwijk, Noordwijk en de Bollenstreek: grachtenpanden, studentenkamers en gezinswoningen. Wij kijken vooraf welke wagen in de straat past en of een verhuislift handiger is dan het trappenhuis.",
  "Groene Hart":
    "In het Groene Hart — Gouda, Alphen aan den Rijn, Bodegraven-Reeuwijk en Waddinxveen — verhuizen we van monumentale panden tot ruime nieuwbouwwoningen, inclusief opslag en ontruiming.",
  Drechtsteden:
    "Dordrecht, Zwijndrecht, Papendrecht en omgeving: van historische binnenstad tot nieuwbouwwijk. Ook zakelijke verhuizingen en transport in de Drechtsteden verzorgen wij zeven dagen per week.",
};

/**
 * Blok dat de directe zoekvragen "verhuizen {stad}", "verhuizing {stad}" en
 * "wat kost verhuizen in {stad}" op de stadspagina zelf beantwoordt.
 * Geen verzonnen tarieven: alleen de factoren die de prijs bepalen.
 */
export type CostBlock = { h2: string; body: string; bullets: string[]; faq: { q: string; a: string }[] };

export function regionCostBlock(region: Region): CostBlock {
  const city = region.city;
  return {
    h2: `Wat kost verhuizen in ${city}?`,
    body: `Er is geen vast tarief voor verhuizen in ${city}: wij rekenen met jouw situatie. Bepalend zijn het aantal kubieke meters inboedel, de verdieping en de bereikbaarheid van beide adressen, de afstand en de extra's die je kiest \u2014 inpakservice, demontage en montage, verhuislift of tijdelijke opslag. Vul het formulier op deze pagina in met je inboedel, dan krijg je een vrijblijvende offerte op maat voor je verhuizing in ${city}, meestal dezelfde dag nog.`,
    bullets: [
      `Hoeveel m\u00b3 inboedel heb je? Het formulier rekent dit voor je uit`,
      `Op welke verdieping woon je in ${city} \u2014 lift, trappenhuis of verhuislift?`,
      `Hoe ver is je nieuwe adres?`,
      `Wil je inpakservice, demontage en montage of opslag erbij?`,
      `Op welke dag wil je verhuizen? Wij zijn zeven dagen per week bereikbaar`,
    ],
    faq: [
      {
        q: `Wat kost een verhuizing in ${city} ongeveer?`,
        a: `De prijs hangt af van je inboedel in m\u00b3, de verdieping, de bereikbaarheid en de extra's. Vul het inboedelformulier in, dan krijg je een offerte op maat voor ${city} \u2014 vrijblijvend en zonder verrassingen achteraf.`,
      },
      {
        q: `Wat kost een verhuislift in ${city}?`,
        a: `Een verhuislift huren in ${city} kan al vanaf \u20ac 95 per uur, inclusief ervaren bediener, tot acht hoog en 26 meter. Bij een complete verhuizing nemen we de lift mee in \u00e9\u00e9n totaalprijs.`,
      },
      {
        q: `Kan ik goedkoper verhuizen in ${city}?`,
        a: `Ja: zelf inpakken, flexibel zijn met de dag en kleine spullen zelf meenemen drukken de prijs. Wij denken met je mee over wat je zelf doet en wat je aan ons overlaat in ${city}.`,
      },
    ],
  };
}
